import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { decryptData } from '../utils/cryptoUtils';
import './EditUsersPage.css';

const EditUsersPage = () => {
  const { encryptionKey, getUsers, deleteUser, updateUser } = useContext(AuthContext); // Access encryptionKey
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', cardNumber: '', userType: '', education: false, health: false, harvesting: false, communityOutreach: false, council: false, hr: false, communityResources: false, consultation: false, faqEditor: false, cardIssueFlag: false, cardIssueReason: '', cardIssueMessage: '', citizenRegistryStatus: 'unknown' }); // Add this for the permissions checkboxes
  const [submitting, setSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const getUserId = (user) => user?.id || user?._id;

  useEffect(() => {
    const fetchUsers = async () => {
      const usersList = await getUsers();
      setUsers(usersList);
    };
    fetchUsers();
  }, [getUsers]);

  const handleDelete = async (userId) => {
    // Show confirmation dialog before proceeding with deletion
    const confirmDelete = window.confirm("Are you sure you want to delete this user? This action cannot be undone.");
    
    if (confirmDelete) {
      try {
        await deleteUser(userId);
        setUsers(users.filter(user => getUserId(user) !== userId));
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const [modalShake, setModalShake] = useState(false);

  const handleBackdropClick = () => {
    setModalShake(true);
    setTimeout(() => setModalShake(false), 500);
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      setResetMsg('Password must be at least 6 characters.');
      return;
    }
    try {
      const userId = getUserId(editingUser);
      if (!userId) {
        setResetMsg('❌ Missing user id. Please close and re-open this user.');
        return;
      }
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/users/${userId}/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setResetMsg('✅ Password reset successfully.');
        setNewPassword('');
        setShowResetPassword(false);
      } else {
        setResetMsg(`❌ ${data.message}`);
      }
    } catch (err) {
      setResetMsg('❌ Network error.');
    }
  };

  const handleEdit = (user) => {
    try {
      setShowResetPassword(false);
      setNewPassword('');
      setResetMsg('');
      setUpdateError(null);
      setEditingUser(user);
      setEditForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        cardNumber: user.cardNumber || '',
        userType: user.userType || '',
        education: user.education || false,
        health: user.health || false,
        harvesting: user.harvesting || false,
        communityOutreach: user.communityOutreach || false,
        council: user.council || false,
        hr: user.hr || false,
        communityResources: user.communityResources || false,
        consultation: user.consultation || false,
        faqEditor: user.faqEditor || false,
        cardIssueFlag: user.cardIssueFlag || false,
        cardIssueReason: user.cardIssueReason || '',
        cardIssueMessage: user.cardIssueMessage || '',
        citizenRegistryStatus: user.citizenRegistryStatus || 'unknown'
      });
    } catch (error) {
      console.error('Error setting edit form:', error);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const userId = getUserId(editingUser);
      if (!userId) {
        setUpdateError('Missing user id. Please close and re-open this user.');
        return;
      }

      // Clone the form data to avoid manipulating the state directly
      const userData = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        cardNumber: editForm.cardNumber,
        userType: editForm.userType,
        education: editForm.education,
        health: editForm.health,
        harvesting: editForm.harvesting,
        communityOutreach: editForm.communityOutreach,
        council: editForm.council,
        hr: editForm.hr,
        communityResources: editForm.communityResources,
        consultation: editForm.consultation,
        faqEditor: editForm.faqEditor,
        cardIssueFlag: editForm.cardIssueFlag,
        cardIssueReason: editForm.cardIssueReason,
        cardIssueMessage: editForm.cardIssueMessage,
        citizenRegistryStatus: editForm.citizenRegistryStatus,
      };
      
      // Show loading indicator or disable the submit button
      setSubmitting(true);
      
      await updateUser(userId, userData);
      
      // Update the local users list with the new data
      setUsers(users.map(user => 
        getUserId(user) === userId
          ? { ...user, ...userData } 
          : user
      ));
      
      setEditingUser(null);
      setSubmitting(false);
    } catch (error) {
      console.error('Error updating user:', error);
      setSubmitting(false);
      // Show error message to the user
      setUpdateError('Failed to update user. Please try again.');
    }
  };

  return (
    <div className="admin-container">
      <h2>User Management</h2>

      <div className="user-search-bar">
        <input
          type="text"
          placeholder="Search by name, email, card number or user type…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="user-search-input"
        />
        {searchQuery && (
          <button className="user-search-clear" onClick={() => setSearchQuery('')}>✕</button>
        )}
      </div>

      <div className="table-container">
        <table className="user-table">
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Card Number</th>
              <th>User Type</th>
              <th>Card Issue</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter((user) => {
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase();
                return (
                  (user.firstName || '').toLowerCase().includes(q) ||
                  (user.lastName  || '').toLowerCase().includes(q) ||
                  (user.email     || '').toLowerCase().includes(q) ||
                  (user.cardNumber|| '').toLowerCase().includes(q) ||
                  (user.userType  || '').toLowerCase().includes(q)
                );
              })
              .map(user => (
              <tr key={getUserId(user)}>
                <td>{user.firstName || 'N/A'}</td>
                <td>{user.lastName || 'N/A'}</td>
                <td>{user.email || 'N/A'}</td>
                <td>{user.cardNumber || 'N/A'}</td>
                <td>{user.userType || 'N/A'}</td>
                <td>{user.cardIssueFlag ? 'Flagged' : 'Clear'}</td>
                <td>
                  <button onClick={() => handleEdit(user)}>Edit</button>
                  <button onClick={() => handleDelete(getUserId(user))}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingUser && (
        <div className="edit-modal" onClick={handleBackdropClick}>
          <div className={`edit-modal-content${modalShake ? ' modal-shake' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editForm.firstName} {editForm.lastName}</h3>
              <small style={{color:'#888',fontSize:'0.7rem',fontFamily:'monospace'}}>ID: {getUserId(editingUser)}</small>
              <button type="button" className="close-button" onClick={() => setEditingUser(null)}>×</button>
            </div>
            
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={editForm.firstName}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={editForm.lastName}
                    onChange={handleEditChange}
                    required
                  />
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#666', fontSize: '0.85rem' }}>
                    Staff rule: make sure the user's profile data matches the card, otherwise there will be validation problems.
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cardNumber">RSMIN Card Number</label>
                  <input
                    type="text"
                    id="cardNumber"
                    name="cardNumber"
                    value={editForm.cardNumber}
                    onChange={handleEditChange}
                    required
                  />
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#666', fontSize: '0.85rem' }}>
                    Active citizen validation checks the card number together with the profile last name. The data needs to match the card.
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="userType">User Type</label>
                  <select
                    id="userType"
                    name="userType"
                    value={editForm.userType}
                    onChange={handleEditChange}
                  >
                    <option value="user">User</option>
                    <option value="citizen">Citizen</option>
                    <option value="education">Education</option>
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                {/* Access Permissions Section - Compact */}
                <div className="form-section permissions-section">
                  <h4>Access Permissions</h4>
                  <div className="permissions-compact">
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.education || false}
                        onChange={(e) => setEditForm({ ...editForm, education: e.target.checked })}
                      />
                      <span>Education</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.health || false}
                        onChange={(e) => setEditForm({ ...editForm, health: e.target.checked })}
                      />
                      <span>Program Manager</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.harvesting || false}
                        onChange={(e) => setEditForm({ ...editForm, harvesting: e.target.checked })}
                      />
                      <span>Harvesting</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.communityOutreach || false}
                        onChange={(e) => setEditForm({ ...editForm, communityOutreach: e.target.checked })}
                      />
                      <span>Community Outreach</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.council || false}
                        onChange={(e) => setEditForm({ ...editForm, council: e.target.checked })}
                      />
                      <span>Council</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.hr || false}
                        onChange={(e) => setEditForm({ ...editForm, hr: e.target.checked })}
                      />
                      <span>HR</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.communityResources || false}
                        onChange={(e) => setEditForm({ ...editForm, communityResources: e.target.checked })}
                      />
                      <span>Community Resources</span>
                    </label>
                    
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.consultation || false}
                        onChange={(e) => setEditForm({ ...editForm, consultation: e.target.checked })}
                      />
                      <span>Consultation</span>
                    </label>

                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.faqEditor || false}
                        onChange={(e) => setEditForm({ ...editForm, faqEditor: e.target.checked })}
                      />
                      <span>FAQ Editor</span>
                    </label>
                  </div>
                </div>

                {/* Card Issue Section */}
                <div className="form-section permissions-section" style={{ marginTop: '1rem' }}>
                  <h4>Citizen Card Issue Flags</h4>
                  <div className="permissions-compact" style={{ gridTemplateColumns: '1fr' }}>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={editForm.cardIssueFlag || false}
                        onChange={(e) => setEditForm({ ...editForm, cardIssueFlag: e.target.checked })}
                      />
                      <span>Card Issue Flag (show warning popup)</span>
                    </label>
                  </div>

                  <div className="form-group" style={{ marginTop: '0.75rem' }}>
                    <label htmlFor="citizenRegistryStatus">Registry Status</label>
                    <select
                      id="citizenRegistryStatus"
                      name="citizenRegistryStatus"
                      value={editForm.citizenRegistryStatus}
                      onChange={handleEditChange}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="not_found">Not Found</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="cardIssueReason">Issue Reason</label>
                    <input
                      type="text"
                      id="cardIssueReason"
                      name="cardIssueReason"
                      value={editForm.cardIssueReason}
                      onChange={handleEditChange}
                      placeholder="e.g. inactive_registry_card"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="cardIssueMessage">Issue Message</label>
                    <input
                      type="text"
                      id="cardIssueMessage"
                      name="cardIssueMessage"
                      value={editForm.cardIssueMessage}
                      onChange={handleEditChange}
                      placeholder="Message shown to citizen"
                    />
                  </div>
                </div>
                
                {/* Reset Password Section */}
                <div className="form-section" style={{marginTop:'1rem'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <h4 style={{margin:0}}>Password</h4>
                    <button type="button" className="btn btn-secondary" style={{fontSize:'0.8rem',padding:'0.25rem 0.75rem'}} onClick={() => { setShowResetPassword(!showResetPassword); setResetMsg(''); }}>Reset Password</button>
                  </div>
                  {showResetPassword && (
                    <div style={{marginTop:'0.75rem',display:'flex',gap:'0.5rem',alignItems:'center'}}>
                      <input
                        type="password"
                        placeholder="New password (min 6 chars)"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        style={{flex:1,padding:'0.4rem 0.6rem',border:'1px solid #ccc',borderRadius:'4px'}}
                      />
                      <button type="button" className="btn btn-primary" style={{whiteSpace:'nowrap'}} onClick={handleResetPassword}>Set Password</button>
                    </div>
                  )}
                  {resetMsg && <p style={{margin:'0.5rem 0 0',fontSize:'0.85rem'}}>{resetMsg}</p>}
                </div>

                {updateError && <p className="error-message">{updateError}</p>}
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditUsersPage;